import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import SEO from '../../components/common/SEO';
import industrial from '../../assets/images/industrial.png';
import design from '../../assets/images/design.png';
import commercial from '../../assets/images/commercial.png';
import maint from '../../assets/images/maint.png';
import ins1 from '../../assets/images/ins1.png';
import ins2 from '../../assets/images/ins2.png';
import ins3 from '../../assets/images/ins3.png';
import ins4 from '../../assets/images/ins4.png';
import ins5 from '../../assets/images/ins5.png';
import microgrid from '../../assets/images/microgrid.png';
import bess1 from '../../assets/images/bess1.png';
import bess2 from '../../assets/images/bess2.png';
import ws1 from '../../assets/images/ws1.jpg';
import ws2 from '../../assets/images/ws2.jpg';
import ws3 from '../../assets/images/ws3.jpg';
import ws4 from '../../assets/images/ws4.jpg';

const services = [
  {
    id: 'industrial',
    number: '01',
    label: 'Industrial',
    tagline: 'Power at Scale',
    image: industrial,
    description: 'Engineered for the demands of heavy industry and national-level energy generation. We design, build, and operate large-scale solar infrastructure that performs reliably under the most demanding conditions.',
    items: [
      { title: 'Utility-Scale Solar Farms', text: 'Large-scale energy generation for national grids with multi-MW capacity.' },
      { title: 'Grid-Tied Systems', text: 'High-capacity installations seamlessly integrated into existing industrial power networks.' },
      { title: 'Power Purchase Agreements (PPA)', text: 'Tailored financial models that make industrial solar accessible and bankable.' },
      { title: 'Hybrid Energy Systems', text: 'Intelligent integration of solar with existing industrial power sources for uninterrupted supply.' },
    ],
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M2 28h28M6 28V18l6-4v14M14 28V12l6-6v22M22 28V16l6-4v16"/>
      </svg>
    ),
  },
  {
    id: 'design',
    number: '02',
    label: 'Design & Engineering',
    tagline: 'Built from First Principles',
    image: design,
    description: 'Every project begins with rigorous engineering. Our design team combines cutting-edge simulation tools with deep field experience to create solar systems that are optimized before a single panel is installed.',
    items: [
      { title: 'Custom System Architecture', text: 'Bespoke engineering tailored to site-specific conditions, shading, and load profiles.' },
      { title: 'Technical Feasibility Studies', text: 'Comprehensive site analysis and ROI projections backed by real data.' },
      { title: '3D Modeling & Simulation', text: 'Advanced visualization of solar performance, shading analysis, and energy yield forecasting.' },
      { title: 'Procurement Excellence', text: 'Sourcing Tier-1 components for maximum longevity and performance warranties.' },
    ],
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <rect x="4" y="4" width="24" height="24" rx="2"/>
        <path d="M9 16h14M16 9v14M9 9l14 14M23 9L9 23"/>
      </svg>
    ),
  },
  {
    id: 'commercial',
    number: '03',
    label: 'Commercial',
    tagline: 'Solar for Business',
    image: commercial,
    description: 'Whether you run a single office building or a nationwide chain of facilities, our commercial solutions are designed to reduce your energy costs, improve sustainability metrics, and deliver measurable ROI.',
    items: [
      { title: 'Rooftop Solar Installations', text: 'Space-efficient energy solutions for office buildings, factories, and commercial properties.' },
      { title: 'Net Metering', text: 'Systems designed to sell excess power back to the grid, turning your roof into a revenue stream.' },
      { title: 'Off-Grid & Battery Storage', text: 'Reliable backup power for uninterrupted business operations during outages.' },
      { title: 'Corporate Energy Audits', text: 'Identifying efficiency gains and optimal solar sizing for commercial facilities.' },
    ],
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M4 28V12l12-8 12 8v16H4z"/>
        <path d="M12 28v-8h8v8"/>
        <path d="M4 16h24"/>
      </svg>
    ),
  },
  {
    id: 'maintenance',
    number: '04',
    label: 'Maintenance & Support',
    tagline: 'Performance for Life',
    image: maint,
    description: 'A solar installation is only as good as the team maintaining it. Our O&M division ensures every system we build continues to perform at its peak — year after year, with minimal downtime and maximum yield.',
    items: [
      { title: '24/7 Remote Monitoring', text: 'Real-time performance tracking via smart technology and our Network Operations Center.' },
      { title: 'Preventive Maintenance', text: 'Scheduled cleaning, electrical health checks, and inverter inspections.' },
      { title: 'O&M Services', text: 'Full-service Operations and Maintenance for peak efficiency throughout the system lifecycle.' },
      { title: 'System Upgrades', text: 'Modernizing existing plants with the latest inverter and panel technology.' },
    ],
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <circle cx="16" cy="16" r="4"/>
        <path d="M16 4v4M16 24v4M4 16h4M24 16h4"/>
        <path d="M7.76 7.76l2.83 2.83M21.41 21.41l2.83 2.83M7.76 24.24l2.83-2.83M21.41 10.59l2.83-2.83"/>
      </svg>
    ),
  },
];

const steps = [
  {
    number: '01', label: 'Survey', image: ins1,
    description: 'Analyzing energy needs and site feasibility',
    detail: 'Our engineers conduct a thorough on-site survey to assess solar irradiance, shading, structural load capacity, and grid connectivity — laying the foundation for a precise and bankable design.',
  },
  {
    number: '02', label: 'Design', image: ins2,
    description: 'Technical design and system architecture',
    detail: 'Using advanced simulation tools, we develop a fully customized system layout including panel orientation, inverter sizing, cable routing, and 3D yield modeling for maximum performance.',
  },
  {
    number: '03', label: 'Procurement', image: ins3,
    description: 'Sourcing industry-leading, high-efficiency technology',
    detail: 'We source only Tier-1 certified equipment from globally trusted manufacturers — ensuring every component meets our rigorous standards for efficiency, warranty, and long-term reliability.',
  },
  {
    number: '04', label: 'Deployment', image: ins4,
    description: 'Professional installation and grid integration',
    detail: 'Our certified installation teams execute the project with precision — handling civil works, mounting, wiring, inverter commissioning, and full grid synchronization safely and on schedule.',
  },
  {
    number: '05', label: 'O&M Support', image: ins5,
    description: 'Continuous monitoring and operational maintenance',
    detail: 'Post-commissioning, our NOC team provides 24/7 remote monitoring, scheduled preventive maintenance, and rapid on-site response — ensuring your system delivers peak performance for decades.',
  },
];

const bessBullets = [
  'Enables energy time-shifting to align solar generation with load demand',
  'Increases solar self-consumption and reduces energy curtailment',
  'Provides fast-response power support for frequency and voltage regulation',
  'Supports peak shaving, load leveling, and ramp-rate control',
  'Utilizes high-efficiency bidirectional power conversion systems (PCS)',
  'Incorporates advanced battery management systems (BMS) for continuous monitoring',
  'Ensures grid-code compliance through reactive power support',
  'Offers reliable backup power and black-start capability during grid outages',
];

const microgridItems = [
  'Demand charge reduction',
  'EV charging integration',
  'Frequency regulation services',
  'Genset dispatch optimization',
  'Self-consumption & self-sufficiency',
  'Grid limitation management',
];

// scroll reveal wrapper
function FadeIn({ children, delay = 0, threshold = 0.1 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(40px)',
      transition: `opacity 0.8s ease ${delay}ms, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

function Services() {
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  const [stepActive, setStepActive] = useState(0);
  const [stepAnimating, setStepAnimating] = useState(false);
  const [stepVisible, setStepVisible] = useState(false);
  const stepRef = useRef(null);

  const [formData, setFormData] = useState({ from_name: '', phone: '', from_email: '', sector: '', size: '', message: '' });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    function onResize() { setScreenWidth(window.innerWidth); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = screenWidth < 768;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStepVisible(true); },
      { threshold: 0.05 }
    );
    if (stepRef.current) observer.observe(stepRef.current);
    return () => observer.disconnect();
  }, []);

  const handleTabClick = (idx) => {
    if (idx === active || animating) return;
    setAnimating(true);
    setTimeout(() => { setActive(idx); setAnimating(false); }, 220);
  };

  const goToStep = (idx) => {
    if (idx === stepActive || stepAnimating) return;
    setStepAnimating(true);
    setTimeout(() => { setStepActive(idx); setStepAnimating(false); }, 200);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    setSending(true);
    setStatus(null);
    try {
      await emailjs.send('service_j7njzim', 'template_2hnw1f3', formData, '0t3myGNVWRrrx-J7U');
      setStatus('success');
      setFormData({ from_name: '', phone: '', from_email: '', sector: '', size: '', message: '' });
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  const current = services[active];
  const currentStep = steps[stepActive];

  return (
    <div className="bg-white min-h-screen">
      <SEO title="Services" description="Explore FSEL's comprehensive EPC solar energy solutions, maintenance, and power purchase agreements." url="/services" />

      {/* page header */}
      <section style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #0d2240 50%, #0f2d5a 100%)',
        padding: '80px 24px 72px', textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '48px 48px' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#facc15', marginBottom: '16px' }}>What We Offer</p>
          <h1 style={{ fontSize: 'clamp(32px, 6vw, 64px)', fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: '20px' }}>Our Services</h1>
          <div style={{ width: '60px', height: '3px', background: 'linear-gradient(to right, #3b82f6, #facc15)', borderRadius: '2px', margin: '0 auto 20px' }} />
          <p style={{ fontSize: '1rem', color: '#93c5fd', maxWidth: '520px', margin: '0 auto', lineHeight: 1.75 }}>
            End-to-end solar solutions — from feasibility to commissioning to lifetime maintenance.
          </p>
        </div>
      </section>

      {/* why solar section */}
      <section style={{ background: '#ffffff', padding: isMobile ? '60px 16px' : '100px 24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#1e3a8a', marginBottom: '10px' }}>
                Why Solar Energy
              </p>
              <h2 style={{ fontSize: 'clamp(26px, 5vw, 48px)', fontWeight: 700, color: '#0a1628', marginBottom: '16px', lineHeight: 1.2 }}>
                Powering the Future with Solar
              </h2>
              <div style={{ width: '60px', height: '3px', background: 'linear-gradient(to right, #1d4ed8, #facc15)', margin: '0 auto 20px', borderRadius: '2px' }} />
              <p style={{ maxWidth: '650px', margin: '0 auto', fontSize: '15px', color: '#475569', lineHeight: 1.8 }}>
                Solar energy is no longer an alternative — it is a strategic necessity for businesses seeking cost stability,
                energy independence, and long-term sustainability.
              </p>
            </div>
          </FadeIn>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '32px' : '60px',
            alignItems: 'center',
            marginBottom: '80px',
          }}>
            <FadeIn>
              <div>
                <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.9, marginBottom: '24px' }}>
                  Rising electricity costs, unreliable grid infrastructure, and increasing sustainability demands are forcing
                  industries to rethink their energy strategies. Solar energy provides a scalable and future-proof solution
                  that transforms power from a recurring expense into a long-term investment.
                </p>
                <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.9 }}>
                  With advancements in technology and integration with smart energy systems, solar is now capable of delivering
                  consistent performance, financial returns, and operational resilience across industrial and commercial sectors.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div style={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(13,34,64,0.12)', border: '1px solid #e8f0fe' }}>
                <img src={ws1} alt="Solar Energy" style={{ width: '100%', display: 'block' }} />
              </div>
            </FadeIn>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '80px',
          }}>
            {[
              { title: 'Cost Efficiency', text: 'Reduce electricity costs and protect your business from rising energy tariffs.', img: ws1 },
              { title: 'Energy Independence', text: 'Generate your own power and reduce reliance on unstable grid supply.', img: ws3 },
              { title: 'Sustainability', text: 'Meet environmental goals and align with global ESG standards.', img: ws4 },
              { title: 'High ROI', text: 'Transform energy infrastructure into a long-term revenue-generating asset.', img: ws2 },
            ].map((card, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div
                  style={{ background: '#f8faff', borderRadius: '16px', overflow: 'hidden', border: '1px solid #e8f0fe', transition: 'all 0.3s ease', cursor: 'default' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 20px 40px rgba(13,34,64,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <img src={card.img} alt={card.title} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                  <div style={{ padding: '16px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0a1628', marginBottom: '8px' }}>{card.title}</h3>
                    <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.6 }}>{card.text}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ fontSize: 'clamp(20px, 3vw, 32px)', fontWeight: 700, color: '#0a1628', marginBottom: '20px' }}>
                How Solar Power Works
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                gap: '16px',
                maxWidth: '900px',
                margin: '0 auto',
              }}>
                {[
                  'Solar panels capture sunlight',
                  'Inverters convert energy into electricity',
                  'Power is used by your facility',
                  'Excess energy is stored or exported',
                ].map((step, i) => (
                  <div key={i} style={{ padding: '16px', background: '#eff6ff', borderRadius: '12px', border: '1px solid #dbeafe' }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

        </div>
      </section>

      {/* tab section */}
      <section
        ref={sectionRef}
        style={{
          background: '#f8faff',
          padding: isMobile ? '48px 0' : '80px 0',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.9s ease, transform 0.9s ease',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>

          {isMobile ? (
            <div>
              <div style={{ display: 'flex', overflowX: 'auto', gap: '8px', paddingBottom: '16px', marginBottom: '24px', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                {services.map((svc, idx) => {
                  const isActive = idx === active;
                  return (
                    <button
                      key={svc.id}
                      onClick={() => handleTabClick(idx)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 16px', borderRadius: '24px', flexShrink: 0,
                        background: isActive ? '#0a1628' : '#fff',
                        border: isActive ? '2px solid #facc15' : '2px solid #e2e8f0',
                        cursor: 'pointer', transition: 'all 0.25s ease',
                        color: isActive ? '#facc15' : '#64748b',
                      }}
                    >
                      <div style={{ color: isActive ? '#facc15' : '#94a3b8' }}>{svc.icon}</div>
                      <span style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', color: isActive ? '#fff' : '#0a1628' }}>{svc.label}</span>
                    </button>
                  );
                })}
              </div>

              <div style={{
                borderRadius: '20px', overflow: 'hidden',
                boxShadow: '0 12px 40px rgba(13,34,64,0.1)',
                opacity: animating ? 0 : 1,
                transform: animating ? 'translateY(8px)' : 'translateY(0)',
                transition: 'opacity 0.22s ease, transform 0.22s ease',
              }}>
                <div style={{
                  position: 'relative', overflow: 'hidden', minHeight: '200px',
                  backgroundImage: `url(${current.image})`, backgroundSize: 'cover', backgroundPosition: 'center',
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(8,20,48,0.35) 0%, rgba(8,20,48,0.85) 100%)' }} />
                  <div style={{ position: 'relative', zIndex: 1, padding: '24px' }}>
                    <span style={{ display: 'inline-block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#facc15', background: 'rgba(0,0,0,0.35)', padding: '4px 12px', borderRadius: '20px', marginBottom: '10px', backdropFilter: 'blur(4px)' }}>{current.tagline}</span>
                    <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#fff', marginBottom: '8px', lineHeight: 1.2 }}>{current.label}</h2>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>{current.description}</p>
                  </div>
                </div>
                <div style={{ background: '#fff', padding: '20px', display: 'grid', gridTemplateColumns: '1fr', gap: '14px' }}>
                  {current.items.map((item, i) => (
                    <div key={i} style={{ padding: '16px', borderRadius: '14px', background: '#f8faff', border: '1px solid #e8f0fe' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '7px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#facc15', flexShrink: 0 }} />
                        <p style={{ fontSize: '13px', fontWeight: 700, color: '#0a1628', lineHeight: 1.3 }}>{item.title}</p>
                      </div>
                      <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.7, paddingLeft: '16px' }}>{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(13,34,64,0.12)', minHeight: '560px' }}>

              <div style={{ background: '#0a1628', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div style={{ height: '3px', background: 'linear-gradient(to right, #facc15, #3b82f6)', flexShrink: 0 }} />
                <div style={{ padding: '32px 28px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <p style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>Foundation Solar Energy</p>
                </div>
                <nav style={{ flex: 1, padding: '12px 0' }}>
                  {services.map((svc, idx) => {
                    const isActive = idx === active;
                    return (
                      <button key={svc.id} onClick={() => handleTabClick(idx)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 28px', background: isActive ? 'rgba(250,204,21,0.08)' : 'transparent', borderLeft: isActive ? '3px solid #facc15' : '3px solid transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.25s ease', position: 'relative' }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0, background: isActive ? '#facc15' : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isActive ? '#0a1628' : 'rgba(255,255,255,0.45)', transition: 'all 0.25s ease' }}>{svc.icon}</div>
                        <div>
                          <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: isActive ? '#facc15' : 'rgba(255,255,255,0.3)', marginBottom: '2px', transition: 'color 0.25s ease' }}>{svc.number}</p>
                          <p style={{ fontSize: '14px', fontWeight: isActive ? 600 : 400, color: isActive ? '#fff' : 'rgba(255,255,255,0.55)', transition: 'all 0.25s ease', lineHeight: 1.3 }}>{svc.label}</p>
                        </div>
                        {isActive && <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderRight: '10px solid #f8faff' }} />}
                      </button>
                    );
                  })}
                </nav>
                <div style={{ padding: '24px 28px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', lineHeight: 1.6 }}>EPC Solar Solutions<br />Nationwide · Since 2019</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', opacity: animating ? 0 : 1, transform: animating ? 'translateX(12px)' : 'translateX(0)', transition: 'opacity 0.22s ease, transform 0.22s ease' }}>
                <div style={{ position: 'relative', overflow: 'hidden', minHeight: '220px', backgroundImage: `url(${current.image})`, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(8,20,48,0.35) 0%, rgba(8,20,48,0.85) 100%)' }} />
                  <span style={{ position: 'absolute', right: '32px', top: '16px', fontSize: '100px', fontWeight: 700, color: 'rgba(255,255,255,0.06)', lineHeight: 1, userSelect: 'none', letterSpacing: '-6px' }}>{current.number}</span>
                  <div style={{ position: 'relative', zIndex: 1, padding: '32px 44px' }}>
                    <span style={{ display: 'inline-block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#facc15', background: 'rgba(0,0,0,0.35)', padding: '4px 12px', borderRadius: '20px', marginBottom: '12px', backdropFilter: 'blur(4px)' }}>{current.tagline}</span>
                    <h2 style={{ fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 700, color: '#fff', marginBottom: '10px', lineHeight: 1.2 }}>{current.label}</h2>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.75, maxWidth: '520px' }}>{current.description}</p>
                  </div>
                </div>
                <div style={{ background: '#fff', padding: '32px 44px 40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', flex: 1 }}>
                  {current.items.map((item, i) => (
                    <div key={i} style={{ padding: '18px 20px', borderRadius: '14px', background: '#f8faff', border: '1px solid #e8f0fe', transition: 'border-color 0.2s ease, transform 0.2s ease', cursor: 'default' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#facc15'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8f0fe'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '7px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#facc15', flexShrink: 0 }} />
                        <p style={{ fontSize: '13px', fontWeight: 700, color: '#0a1628', lineHeight: 1.3 }}>{item.title}</p>
                      </div>
                      <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.7, paddingLeft: '16px' }}>{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}
        </div>
      </section>

      {/* microgrid solutions section */}
      <section style={{ background: '#fff', padding: isMobile ? '60px 16px' : '90px 24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#1e3a8a', marginBottom: '10px' }}>Advanced Solutions</p>
              <h2 style={{ fontSize: 'clamp(24px, 4vw, 44px)', fontWeight: 700, color: '#0a1628', marginBottom: '14px', lineHeight: 1.15 }}>Microgrid Solutions</h2>
              <div style={{ width: '56px', height: '3px', background: 'linear-gradient(to right, #1d4ed8, #facc15)', borderRadius: '2px', margin: '0 auto' }} />
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '60px', alignItems: 'center' }}>

            <FadeIn delay={0}>
              <div>
                <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.85, marginBottom: '28px' }}>
                  Governments and industry are moving towards renewable energy sources such as solar and wind power. At the same time, advancements in digitalization have already transformed many industries. Microgrids and hybrid systems meet the growing demand for more flexible, sustainable and cost-effective solutions. FSEL's microgrid solutions offer a wide variety of applications and service products, each individually designed to meet your specific needs.
                </p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#0a1628', marginBottom: '16px' }}>
                  FSEL microgrid solutions range from stand-alone battery storage to fully integrated hybrid systems, offering:
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {microgridItems.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', background: '#f0f6ff', borderRadius: '10px', border: '1px solid #dbeafe' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#1d4ed8', flexShrink: 0, marginTop: '6px' }} />
                      <p style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a', lineHeight: 1.4 }}>{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={150}>
              <div style={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(13,34,64,0.1)', border: '1px solid #e8f0fe' }}>
                <img src={microgrid} alt="Microgrid Solutions" style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>
            </FadeIn>

          </div>
        </div>
      </section>

      {/* bess section */}
      <section style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2240 60%, #0f2d5a 100%)', padding: isMobile ? '60px 16px' : '90px 24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#facc15', marginBottom: '10px' }}>Energy Storage</p>
              <h2 style={{ fontSize: 'clamp(22px, 4vw, 44px)', fontWeight: 700, color: '#fff', marginBottom: '14px', lineHeight: 1.15 }}>
                Battery Energy Storage System <span style={{ color: '#facc15' }}>(BESS)</span>
              </h2>
              <div style={{ width: '56px', height: '3px', background: 'linear-gradient(to right, #3b82f6, #facc15)', borderRadius: '2px', margin: '0 auto' }} />
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '60px', alignItems: 'center' }}>

            <FadeIn delay={0}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
                  <img src={bess1} alt="BESS Energy Storage Unit" style={{ width: '100%', height: 'auto', display: 'block' }} />
                </div>
                <div style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
                  <img src={bess2} alt="BESS System Diagram" style={{ width: '100%', height: 'auto', display: 'block' }} />
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={150}>
              <div>
                <p style={{ fontSize: '15px', color: '#bfdbfe', lineHeight: 1.85, marginBottom: '24px' }}>
                  In solar power applications, a <strong style={{ color: '#fff' }}>Battery Energy Storage System (BESS)</strong> is a strategically important component that significantly enhances the technical performance, reliability, and commercial effectiveness of solar energy solutions. BESS addresses the mismatch between solar generation and load demand by storing excess energy during peak irradiance and dispatching it during evening hours or low-generation periods.
                </p>
                <p style={{ fontSize: '13px', fontWeight: 600, color: '#facc15', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '14px' }}>
                  Key Technical Aspects
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
                  {bessBullets.map((bullet, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#facc15', flexShrink: 0, marginTop: '7px' }} />
                      <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>{bullet}</p>
                    </div>
                  ))}
                </div>
                <div style={{ background: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.2)', borderRadius: '14px', padding: '18px 20px' }}>
                  <p style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: 1.75 }}>
                    The integration of BESS transforms solar PV systems into <strong style={{ color: '#facc15' }}>dispatchable, resilient, and intelligent energy assets</strong> — making BESS an essential technology for modern, efficient, and future-ready solar power solutions.
                  </p>
                </div>
              </div>
            </FadeIn>

          </div>
        </div>
      </section>

      {/* process stepper section */}
      <section
        ref={stepRef}
        style={{
          background: 'linear-gradient(180deg, #dbeafe 0%, #eff6ff 60%, #e0f2fe 100%)',
          padding: isMobile ? '48px 0 56px' : '80px 0 90px',
          opacity: stepVisible ? 1 : 0,
          transform: stepVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.9s ease, transform 0.9s ease',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#1e3a8a', marginBottom: '12px' }}>How We Work</p>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 44px)', fontWeight: 700, color: '#0a1628', marginBottom: '14px', lineHeight: 1.15 }}>The Process</h2>
            <div style={{ width: '56px', height: '3px', background: 'linear-gradient(to right, #1d4ed8, #facc15)', borderRadius: '2px', margin: '0 auto' }} />
          </div>

          <div style={{ overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'center', minWidth: isMobile ? 'max-content' : 'auto', padding: isMobile ? '0 8px 8px' : '0', gap: '0' }}>
              {steps.map((step, idx) => {
                const isActive = idx === stepActive;
                const isDone = idx < stepActive;
                return (
                  <React.Fragment key={step.number}>
                    <button onClick={() => goToStep(idx)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: isActive ? '3px solid #1d4ed8' : isDone ? '3px solid #1d4ed8' : '2px solid #bfdbfe', background: isActive ? '#1d4ed8' : isDone ? '#dbeafe' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s ease', boxShadow: isActive ? '0 0 0 6px rgba(29,78,216,0.12)' : 'none' }}>
                        {isDone ? <svg width="18" height="18" fill="none" stroke="#1d4ed8" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
                          : <span style={{ fontSize: '13px', fontWeight: 700, color: isActive ? '#fff' : '#93c5fd' }}>{step.number}</span>}
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: isActive ? 700 : 500, color: isActive ? '#1d4ed8' : '#64748b', whiteSpace: 'nowrap', transition: 'all 0.3s ease' }}>{step.label}</span>
                    </button>
                    {idx < steps.length - 1 && <div style={{ width: isMobile ? '40px' : '140px', height: '2px', marginBottom: '22px', background: idx < stepActive ? '#1d4ed8' : '#bfdbfe', transition: 'background 0.4s ease', flexShrink: 0 }} />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div style={{
            background: '#fff', borderRadius: '20px', overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(13,34,64,0.1)',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            minHeight: isMobile ? 'auto' : '340px',
            opacity: stepAnimating ? 0 : 1,
            transform: stepAnimating ? 'translateY(10px)' : 'translateY(0)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
          }}>
            <div style={{ position: 'relative', overflow: 'hidden', backgroundImage: `url(${currentStep.image})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: isMobile ? '200px' : '300px' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(8,20,48,0.5) 0%, rgba(8,20,48,0.2) 100%)' }} />
              <div style={{ position: 'absolute', top: '20px', left: '20px', background: '#facc15', color: '#0a1628', fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', padding: '6px 14px', borderRadius: '20px', textTransform: 'uppercase' }}>Step {currentStep.number}</div>
            </div>
            <div style={{ padding: isMobile ? '28px 20px' : '48px 52px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#1d4ed8', marginBottom: '14px' }}>{currentStep.number} / 05</p>
              <h3 style={{ fontSize: 'clamp(20px, 3vw, 30px)', fontWeight: 700, color: '#0a1628', marginBottom: '12px', lineHeight: 1.2 }}>{currentStep.label}</h3>
              <p style={{ fontSize: '14px', fontWeight: 600, color: '#1d4ed8', marginBottom: '16px' }}>{currentStep.description}</p>
              <div style={{ width: '40px', height: '3px', background: '#facc15', borderRadius: '2px', marginBottom: '20px' }} />
              <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.8 }}>{currentStep.detail}</p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
                <button onClick={() => goToStep(Math.max(0, stepActive - 1))} disabled={stepActive === 0}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #bfdbfe', background: 'transparent', color: stepActive === 0 ? '#bfdbfe' : '#1d4ed8', fontSize: '13px', fontWeight: 600, cursor: stepActive === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }}
                  onMouseEnter={e => { if (stepActive !== 0) e.currentTarget.style.background = '#eff6ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
                  Previous
                </button>
                <button onClick={() => goToStep(Math.min(steps.length - 1, stepActive + 1))} disabled={stepActive === steps.length - 1}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '10px', border: 'none', background: stepActive === steps.length - 1 ? '#e2e8f0' : '#1d4ed8', color: stepActive === steps.length - 1 ? '#94a3b8' : '#fff', fontSize: '13px', fontWeight: 600, cursor: stepActive === steps.length - 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }}
                  onMouseEnter={e => { if (stepActive !== steps.length - 1) e.currentTarget.style.background = '#1e40af'; }}
                  onMouseLeave={e => { if (stepActive !== steps.length - 1) e.currentTarget.style.background = '#1d4ed8'; }}>
                  Next
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* free quotation section */}
      <section className="py-16 sm:py-24 bg-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Don't Know What To{' '}<span className="text-blue-700">Start With?</span><br />
                <span className="text-2xl sm:text-4xl font-semibold text-gray-700">Get a Solution For All Your{' '}<span className="text-yellow-500">Solar Needs</span></span>
              </h2>
              <p className="text-gray-500 text-base sm:text-lg leading-relaxed">Fill in your details and our team will get back to you with a customized solar solution — completely free of charge.</p>
              <div className="mt-8 w-20 h-1 bg-gradient-to-r from-blue-600 to-yellow-400 rounded-full"></div>
            </div>
            <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-6 sm:p-10 shadow-xl border border-blue-800">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Full Name</label>
                  <input type="text" name="from_name" value={formData.from_name} onChange={handleChange} placeholder="Your Name" className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+92 300 0000000" className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Email Address</label>
                  <input type="email" name="from_email" value={formData.from_email} onChange={handleChange} placeholder="youremail@example.com" className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Sector</label>
                  <select name="sector" value={formData.sector} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none">
                    <option value="" disabled>Select Sector</option>
                    <option value="Residential">Residential</option>
                    <option value="Industrial/Commercial">Industrial/Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">System Size</label>
                  <select name="size" value={formData.size} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none">
                    <option value="" disabled>Select Size</option>
                    <option value="5kw-25kw">5kw - 25kw</option>
                    <option value="25kw-100kw">25kw - 100kw</option>
                    <option value="100kw-1MW">100kw - 1MW</option>
                    <option value="1MW-10MW">1MW - 10MW</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Service Description</label>
                  <textarea rows={4} name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your energy needs, project size, location..." className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none" />
                </div>
                {status && (
                  <p className={`text-sm font-semibold text-center ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                    {status === 'success' ? '✓ Your quote request has been sent! We will get back to you soon.' : '✗ Something went wrong. Please try again.'}
                  </p>
                )}
                <button type="button" onClick={handleSubmit} disabled={sending} className="w-full py-4 bg-gradient-to-r from-blue-700 to-blue-900 text-white font-bold text-lg rounded-xl hover:from-blue-600 hover:to-blue-800 transform hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-blue-500/40 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100">
                  {sending ? 'Sending...' : 'Get your Quote'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Services;